import React from "react";

import { CircleCheck, CircleX, TriangleAlert, Wrench } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "../../../lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../ui/accordion";
import { Badge } from "../../ui/badge";

import type { JSX } from "react";

import type {
  ValidateEpgFinding,
  ValidateEpgResponse,
  ValidateEpgSeverity,
} from "../../../api/MediaAPI.types";

const SEVERITY_ORDER = [
  "error",
  "warning",
  "info",
] as const satisfies readonly ValidateEpgSeverity[];

interface SeverityTone {
  badge: string;
  dot: string;
}

const SEVERITY_TONE: Record<ValidateEpgSeverity, SeverityTone> = {
  error: {
    badge: "border-status-error/40 text-status-error",
    dot: "bg-status-error",
  },
  warning: {
    badge: "border-status-warning/40 text-status-warning",
    dot: "bg-status-warning",
  },
  info: {
    badge: "border-status-info/40 text-status-info",
    dot: "bg-status-info",
  },
};

const getSeverityTone = (severity: ValidateEpgSeverity): SeverityTone =>
  SEVERITY_TONE[severity] ?? SEVERITY_TONE.info;

const getSeverityRank = (severity: ValidateEpgSeverity): number => {
  const rank = SEVERITY_ORDER.indexOf(severity);
  return rank === -1 ? SEVERITY_ORDER.length : rank;
};

const findBySeverity = (
  findings: ValidateEpgFinding[],
  severity: ValidateEpgSeverity,
): ValidateEpgFinding[] =>
  findings.filter((finding) => finding.severity === severity);

const getSeverities = (findings: ValidateEpgFinding[]): ValidateEpgSeverity[] =>
  [...new Set(findings.map((finding) => finding.severity))].sort(
    (a, b) => getSeverityRank(a) - getSeverityRank(b),
  );

const getSummaryKey = (result: ValidateEpgResponse): string => {
  if (!result.valid) return "epgValidation.summaryInvalid";
  if (result.findings.some((finding) => finding.severity === "warning")) {
    return "epgValidation.summaryValidWithWarnings";
  }
  return "epgValidation.summaryValid";
};

interface FindingListProps {
  findings: ValidateEpgFinding[];
  severity: ValidateEpgSeverity;
}

function FindingList({ findings, severity }: FindingListProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const tone = getSeverityTone(severity);

  return (
    <Accordion type="multiple">
      {findings.map((finding) => {
        const samples = [...new Set(finding.samples)];
        const remaining = finding.count - finding.samples.length;
        const why = t(`epgValidation.${severity}.${finding.code}.why`, {
          defaultValue: "",
        });
        const fix = t(`epgValidation.${severity}.${finding.code}.fix`, {
          defaultValue: "",
        });

        return (
          <AccordionItem
            key={finding.code}
            className="border-b-0"
            value={finding.code}
          >
            <AccordionTrigger className="cursor-pointer gap-2 py-2 hover:no-underline">
              <span className="flex flex-1 items-center gap-2">
                <span
                  className={cn("size-1.5 shrink-0 rounded-full", tone.dot)}
                />
                <span className="font-medium">
                  {t(`epgValidation.${severity}.${finding.code}.label`, {
                    defaultValue: finding.code,
                  })}
                </span>
              </span>
              <Badge
                className={cn("font-normal", tone.badge)}
                variant="outline"
              >
                {t("epgValidation.occurrences", { count: finding.count })}
              </Badge>
            </AccordionTrigger>

            <AccordionContent className="flex flex-col gap-2 pb-3 pl-3.5">
              {why ? <p className="text-subtle">{why}</p> : null}

              {fix ? (
                <div className="border-border bg-surface-muted flex gap-2 rounded-md border p-2.5">
                  <Wrench className="text-subtle mt-0.5 size-3.5 shrink-0" />
                  <p>
                    <span className="font-medium">
                      {t("epgValidation.fixLabel")}
                    </span>{" "}
                    {fix}
                  </p>
                </div>
              ) : null}

              {samples.length > 0 ? (
                <div className="border-border rounded-md border">
                  <div className="divide-border max-h-36 divide-y overflow-y-auto">
                    {samples.map((sample) => (
                      <p
                        key={sample}
                        className="px-2.5 py-1.5 font-mono text-xs break-all"
                      >
                        {sample}
                      </p>
                    ))}
                  </div>
                  {remaining > 0 ? (
                    <p className="text-subtle border-border border-t px-2.5 py-1.5 text-xs">
                      {t("epgValidation.moreOccurrences", { count: remaining })}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

interface EpgValidationReportProps {
  result?: ValidateEpgResponse;
  hasFailed?: boolean;
}

export default function EpgValidationReport({
  result,
  hasFailed,
}: EpgValidationReportProps): JSX.Element {
  const { t } = useTranslation("editor-content");

  const StatusIcon = result?.valid ? CircleCheck : CircleX;

  return (
    <div aria-live="polite" className="max-w-3xl">
      {hasFailed ? (
        <div className="border-border bg-surface-muted text-status-warning flex items-center gap-1.25 rounded-md border p-2.5 text-sm">
          <TriangleAlert className="size-4 shrink-0" />
          {t("epgValidation.requestFailed")}
        </div>
      ) : null}

      {result ? (
        <div className="border-border rounded-lg border">
          <div className="flex flex-col gap-2.5 p-3.5">
            <div className="flex items-center gap-2.5">
              <span
                className={cn(
                  "flex size-7 shrink-0 items-center justify-center rounded-full",
                  result.valid
                    ? "bg-status-active/15 text-status-active"
                    : "bg-status-error/15 text-status-error",
                )}
              >
                <StatusIcon className="size-4" />
              </span>

              <div className="flex flex-col gap-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    {t(
                      result.valid
                        ? "epgValidation.valid"
                        : "epgValidation.invalid",
                    )}
                  </span>
                  {getSeverities(result.findings).map((severity) => (
                    <Badge
                      key={severity}
                      variant="outline"
                      className={cn(
                        "font-normal",
                        getSeverityTone(severity).badge,
                      )}
                    >
                      {t(`epgValidation.${severity}Count`, {
                        count: findBySeverity(result.findings, severity).length,
                      })}
                    </Badge>
                  ))}
                </div>
                <p className="text-subtle text-sm">
                  {t(getSummaryKey(result))}
                </p>
              </div>
            </div>

            {getSeverities(result.findings).map((severity) => (
              <FindingList
                key={severity}
                findings={findBySeverity(result.findings, severity)}
                severity={severity}
              />
            ))}
          </div>

          {result.source ? (
            <div className="border-border text-subtle flex flex-wrap gap-3.5 border-t px-3.5 py-2 text-xs">
              <span>
                {t("epgValidation.statChannels", {
                  count: result.source.channels,
                })}
              </span>
              <span>
                {t("epgValidation.statProgrammes", {
                  count: result.source.programmes,
                })}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
